import { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ConfigurationTable, ColumnDefinition } from './ConfigurationTable';
import { useAtom } from 'jotai';
import { paintBlockFields, riskConfigData } from '../state';
import { stripTypenameFromRemediationConfig } from '../utils';
import {
  RemediationScoreAreaField,
  RemediationScoreAreaSection,
  TemplateTypeEnum,
  useAllTemplatesForBuilderLazyQuery,
  useUpdateRemediationScoreConfigMutation,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { humanizeString } from '@/utils/string';

// Type definitions
type AreaConfigItem = {
  id: string;
  fieldKey: string;
  threshold: number;
  score: number;
};

export const AreaConfiguration = ({
  structureId,
  templateId,
}: {
  structureId: string;
  templateId: string;
}) => {
  const [areaItems, setAreaItems] = useState<AreaConfigItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [allPaintBlockTemplates, setAllPaintBlockTemplates] = useAtom(paintBlockFields);
  const [riskConfig, setRiskConfig] = useAtom(riskConfigData);

  const [getAllPaintBlockTemplates, { data, loading }] = useAllTemplatesForBuilderLazyQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.PaintBlocks,
    },
    fetchPolicy: 'cache-first',
  });

  const [updateRemediationScoreConfig] = useUpdateRemediationScoreConfigMutation();

  useEffect(() => {
    if (!allPaintBlockTemplates) {
      getAllPaintBlockTemplates();
    }
  }, [getAllPaintBlockTemplates, structureId, allPaintBlockTemplates]);

  useEffect(() => {
    if (data?.allTemplates && !allPaintBlockTemplates) {
      setAllPaintBlockTemplates(data.allTemplates.at(0)?.fields);
    }
  }, [data, allPaintBlockTemplates, setAllPaintBlockTemplates]);

  useEffect(() => {
    if (riskConfig && riskConfig.area && riskConfig.area.fields) {
      const convertedItems: AreaConfigItem[] = riskConfig.area.fields.map((field) => ({
        id: field.fieldKey,
        fieldKey: field.fieldKey,
        threshold: field.threshold,
        score: field.score,
      }));
      setAreaItems(convertedItems);
    }
  }, [riskConfig]);

  const columns: ColumnDefinition<AreaConfigItem>[] = [
    {
      key: 'fieldKey',
      label: 'Substrate or Coating',
      width: 3,
      type: 'select',
      options:
        allPaintBlockTemplates?.map((field) => ({
          value: field.key ?? '',
          label: humanizeString(field.key ?? ''),
        })) || [],
    },
    {
      key: 'threshold',
      label: 'Area Threshold (sq m)',
      width: 3,
      type: 'number',
      inputProps: { step: 0.01, min: 0 },
    },
    {
      key: 'score',
      label: 'Score',
      width: 3,
      type: 'number',
      inputProps: { step: 0.01, min: 0 },
    },
  ];

  const handleAddNew = (newItem: Omit<AreaConfigItem, 'id'>) => {
    const itemWithId: AreaConfigItem = {
      ...newItem,
      id: newItem.fieldKey,
      score: typeof newItem.score === 'string' ? parseFloat(newItem.score) : newItem.score,
    };
    const updatedItems = [...areaItems, itemWithId];
    setAreaItems(updatedItems);
    saveAreaConfiguration(updatedItems);
  };

  const handleEditStart = (item: AreaConfigItem) => {
    setEditingId(item.id);
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };

  const handleEditSave = (
    id: string,
    field: keyof Omit<AreaConfigItem, 'id'>,
    value: string | number
  ) => {
    const updatedItems = areaItems.map((item) =>
      item.id === id
        ? {
            ...item,
            [field]: field === 'fieldKey' ? value : parseFloat(value as string),
          }
        : item
    );
    setAreaItems(updatedItems);
    setEditingId(null);
    saveAreaConfiguration(updatedItems);
  };

  const handleInlineChange = (
    id: string,
    field: keyof Omit<AreaConfigItem, 'id'>,
    value: string | number
  ) => {
    setAreaItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: field === 'fieldKey' ? value : parseFloat(value as string),
            }
          : item
      )
    );
  };

  const handleDelete = (item: AreaConfigItem) => {
    const updatedItems = areaItems.filter((areaItem) => areaItem.id !== item.id);
    setAreaItems(updatedItems);
    saveAreaConfiguration(updatedItems);
  };

  const saveAreaConfiguration = async (itemsToSave?: AreaConfigItem[]) => {
    const items = itemsToSave || areaItems;
    if (items.length === 0) return;

    const areaFields: RemediationScoreAreaField[] = items.map((item) => ({
      fieldKey: item.fieldKey,
      threshold: Number(item.threshold),
      score: Number(item.score),
    }));

    const areaData: RemediationScoreAreaSection = {
      fields: areaFields,
    };

    try {
      const response = await updateRemediationScoreConfig({
        variables: {
          input: {
            structureId,
            templateId,
            remediationScoreConfig: {
              area: areaData,
              poi: riskConfig.poi,
              cof: riskConfig.cof,
            },
          },
        },
      });

      // Update atom state with latest DB values
      if (response.data?.updateRemediationScoreConfig?.remediationScoreConfig) {
        const cleanConfig = stripTypenameFromRemediationConfig(
          response.data.updateRemediationScoreConfig.remediationScoreConfig
        );
        setRiskConfig(cleanConfig);
      }
    } catch (error) {
      console.error('Failed to save Area configuration:', error);
    }
  };

  return (
    <>
      <Typography variant="h4">Area Configuration</Typography>

      {loading && <PageLoader />}

      {!loading && (
        <ConfigurationTable
          items={areaItems}
          columns={columns}
          editingId={editingId}
          emptyMessage="No area configurations added yet. Click '+ Add New Row' to create one."
          onEditStart={handleEditStart}
          onEditCancel={handleEditCancel}
          onEditSave={handleEditSave}
          onInlineChange={handleInlineChange}
          onAddNew={handleAddNew}
          onDelete={handleDelete}
        />
      )}

      <Stack direction="row" alignItems="center">
        <Typography variant="h6">Area Equation for asset: </Typography>
        <Typography variant="body2" ml={1}>
          (Parameter Area / Area Threshold) * Score
        </Typography>
      </Stack>
    </>
  );
};
